import { Column } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { decimalNumber } from './decimal-number.transformer';

/**
 * PD-17610 — the tenant's MillionVerifier settings: when Workd checks an
 * email address and how much it may spend doing so. At most one live row per
 * tenant.
 *
 * The API key is NOT here: it lives encrypted in the instance `_secret_store`
 * (the Motive pattern), and connection state is the key's existence there.
 * The row is kept on disconnect, so reconnecting keeps the window and the
 * cap the admin chose. Credits left are read live from MillionVerifier when
 * the settings card loads (a free call), so nothing rewrites this row in the
 * background and it stays audited: a change to the cap or the window is
 * worth a trail.
 */
@Entity('millionverifier_connection')
export class MillionVerifierConnectionEntity extends BaseEntity {
	/** Re-check an address whose last check is older than this many days. */
	@Column('int', { name: 'windowDays', unsigned: true, default: 90 })
	public windowDays!: number;

	/**
	 * Check again right before each email step when the last check is older
	 * than the window. Stored now; the sequence engine (PD-17614) reads it.
	 */
	@Column({ type: 'boolean', name: 'recheckBeforeEachStep', width: 1, default: () => "'1'" })
	public recheckBeforeEachStep!: boolean;

	/** Dollars a calendar month (tenant time zone) Workd may spend without asking. */
	@Column('decimal', { name: 'monthlyCapAmount', precision: 10, scale: 2, default: 25, transformer: decimalNumber })
	public monthlyCapAmount!: number;

	/** What one check costs the tenant, in dollars, as the admin stated it. */
	@Column('decimal', { name: 'costPerCheck', precision: 10, scale: 6, default: 0.004, transformer: decimalNumber })
	public costPerCheck!: number;

	@Column('datetime', { name: 'connectedDate', nullable: true })
	public connectedDate!: Date | null;

	/** Tenant user (user._id) who connected MillionVerifier. */
	@Column('int', { name: 'fk_user__id_connectedBy', unsigned: true, nullable: true })
	public fkUserIdConnectedBy!: number | null;

	@Column('varchar', { name: 'connectedByName', length: 255, nullable: true })
	public connectedByName!: string | null;
}
